/**
 * useSyncEvents.ts
 *
 * Listens for messages posted by the Service Worker and for the custom DOM
 * event dispatched by processMutations() (direct-mode fallback).
 *
 * Returns:
 *  - conflicts: list of ConflictRecord objects pending user resolution
 *  - refreshConflicts(): manually reload conflicts from IndexedDB
 *  - resolveKeepLocal(conflict): force-pushes the local version to the server
 *  - resolveKeepServer(conflict): discards the local version
 */

import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import fetchClient from 'interfaces/fetchClient';
import { offlineDb, ConflictRecord } from 'utils/offlineDb';

export interface UseSyncEventsResult {
    conflicts: ConflictRecord[];
    refreshConflicts: () => Promise<void>;
    resolveKeepLocal: (conflict: ConflictRecord) => Promise<void>;
    resolveKeepServer: (conflict: ConflictRecord) => Promise<void>;
}

export const useSyncEvents = (): UseSyncEventsResult => {
    const [conflicts, setConflicts] = useState<ConflictRecord[]>([]);

    const refreshConflicts = useCallback(async () => {
        const pending = await offlineDb.getPendingConflicts();
        setConflicts(pending);
    }, []);

    // ── Listen to Service Worker postMessages ────────────────────────────────
    useEffect(() => {
        if (!('serviceWorker' in navigator)) return;

        const handleSwMessage = (event: MessageEvent) => {
            const { type, syncedCount, conflictCount } = event.data || {};

            if (type === 'SYNC_COMPLETE') {
                if (syncedCount > 0) {
                    toast.success(
                        `✅ ${syncedCount} change${syncedCount > 1 ? 's' : ''} synced in the background!`,
                    );
                }
                if (conflictCount > 0) {
                    toast.warn(
                        `⚠️ ${conflictCount} conflict${conflictCount > 1 ? 's' : ''} detected — please review.`,
                        { autoClose: false },
                    );
                    refreshConflicts();
                }
            }

            if (type === 'SYNC_CONFLICT') {
                refreshConflicts();
            }
        };

        navigator.serviceWorker.addEventListener('message', handleSwMessage);
        return () => {
            navigator.serviceWorker.removeEventListener('message', handleSwMessage);
        };
    }, [refreshConflicts]);

    // ── Listen to the custom DOM event (direct-mode fallback) ─────────────────
    useEffect(() => {
        const handleDomConflict = () => refreshConflicts();
        window.addEventListener('toola:sync-conflict', handleDomConflict);
        return () => window.removeEventListener('toola:sync-conflict', handleDomConflict);
    }, [refreshConflicts]);

    // ── Load conflicts on mount ───────────────────────────────────────────────
    useEffect(() => {
        refreshConflicts();
    }, [refreshConflicts]);

    // ── Resolution handlers ───────────────────────────────────────────────────

    const resolveKeepLocal = useCallback(async (conflict: ConflictRecord) => {
        const token = conflict.mutation.token || localStorage.getItem('token') || '';
        const orderId = conflict.orderId;
        const body = { ...conflict.localVersion };
        delete body._id;

        try {
            await fetchClient.put(`/orders/${orderId}`, body, {
                headers: { Authorization: `Bearer ${token}` },
            });
            toast.success('✅ Your version has been saved.');
        } catch {
            toast.error('⛔ Could not save your version. Please try again.');
            return;
        }

        if (conflict.id !== undefined) await offlineDb.deleteConflict(conflict.id as number);
        await refreshConflicts();
    }, [refreshConflicts]);

    const resolveKeepServer = useCallback(async (conflict: ConflictRecord) => {
        // Update the local cache with the server version so the UI is consistent
        if (conflict.serverVersion?._id) {
            await offlineDb.cacheOrder(conflict.serverVersion);
        }

        if (conflict.id !== undefined) await offlineDb.deleteConflict(conflict.id as number);
        toast.info('ℹ️ Server version kept. Your local changes have been discarded.');
        await refreshConflicts();
    }, [refreshConflicts]);

    return { conflicts, refreshConflicts, resolveKeepLocal, resolveKeepServer };
};
